import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import {
  fetchHomepageIfNeeded,
  fetchHomepageTopicDetailsIfNeeded,
  fetchHomepageEngagementNetworkIfNeeded,
  fetchHomepageDemoIfNeeded,
  setSelectedSidebarNav,
  logout
} from '../../actions'
import {
  makeHomepageTopicIdSelector,
  makeHomepageTopicDetailsSelector,
  makeHomepageTopicPlanSelector,
  makeHomepageEngagementNetworkSelector,
  makeHomepageDemographicsSelector
} from '../../selectors'
import SidebarComponent from '../../components/sidebar/SidebarComponent'
import HomepageComponent from '../../components/homepage/HomepageComponent'
import Profile from '../../classes/profile'
import { LS_HOMEPAGE_KEY } from '../../constants/local_storage'
import GoogleAnalytics from '../../classes/GoogleAnalytics'
import { HOME_PAGE } from '../../constants/commons'

class HomepageContainer extends React.Component {
  static propTypes = {
    isAdmin: PropTypes.bool.isRequired,
    profile: Profile.PropTypes,
    homepage: PropTypes.object,
    topicId: PropTypes.string,
    topic: PropTypes.object,
    history: PropTypes.object,
    logout: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    shortcut: PropTypes.shape({
      id: PropTypes.string.isRequired,
      expires_at: PropTypes.string.isRequired,
      target: PropTypes.string.isRequired,
      target_id: PropTypes.string.isRequired
    }),
    homepagePlan: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    metrics: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    demographics: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    whatsNew: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    topShows: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    trendingVideos: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    anticipatedShows: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    fetchHomepage: PropTypes.func.isRequired,
    fetchHomepageTopicDetails: PropTypes.func.isRequired,
    fetchHomepageEngagementNetwork: PropTypes.func.isRequired,
    fetchHomepageDemo: PropTypes.func.isRequired,
    selectedNav: PropTypes.string,
    isContentIntelligence: PropTypes.bool,
    isCatalogAnalysis: PropTypes.bool,
    isPerformanceAnalysis: PropTypes.bool,
    isAIInsights: PropTypes.bool,
    selectedSidebarNav: PropTypes.func
  }

  constructor (props) {
    super(props)

    this.state = {
      anticipatedShows: this.props.anticipatedShows
    }
  }

  componentDidMount () {
    GoogleAnalytics.trackGA(HOME_PAGE, this.props.location, this.props.profile)
    this.fetchData ()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.topicId !== this.props.topicId) {
      this.fetchData ()
    }
    if (prevProps.homepagePlan !== this.props.homepagePlan && this.props.homepagePlan.details) {
      this.setState({ anticipatedShows: this.props.homepagePlan })
    }
    if (prevProps.anticipatedShows !== this.props.anticipatedShows && this.props.anticipatedShows.details) {
      this.setState({ anticipatedShows: this.props.anticipatedShows })
    }
    if (prevProps.topic !== this.props.topic && this.props.topic.details) {
      this.props.fetchHomepageEngagementNetwork()
    }
  }

  fetchData = () => {
    const homepage = JSON.parse(localStorage.getItem(LS_HOMEPAGE_KEY))
    if (!homepage) {
      if (this.props.profile && this.props.profile.groups.length > 0) {
        const groups = this.props.profile.groups
        const groupName = groups.filter(group => group !== 'Admin')[0]
        this.props.fetchHomepage(groupName)
      }
    }
    this.props.fetchHomepageTopicDetails()
    this.props.fetchHomepageDemo()
  }

  render () {
    const {profile, topic, location, selectedNav, isContentIntelligence, isCatalogAnalysis, isPerformanceAnalysis, isAIInsights} = this.props
    const homepage = JSON.parse(localStorage.getItem(LS_HOMEPAGE_KEY))
    const myLogout = () => this.props.logout()
    return (
      <>
        <SidebarComponent
          isAdmin={this.props.isAdmin}
          profile={profile}
          topic={topic}
          logout={myLogout}
          location={location}
          shortcut={this.props.shortcut}
          homepage={homepage ? homepage : this.props.homepage}
          selectedSidebarNav={this.props.selectedSidebarNav}
          selectedNav={selectedNav}
          isContentIntelligence={isContentIntelligence}
          isCatalogAnalysis={isCatalogAnalysis}
          isPerformanceAnalysis={isPerformanceAnalysis}
          isAIInsights={isAIInsights}
          />
        <HomepageComponent
          isAdmin={this.props.isAdmin}
          demographics={this.props.demographics}
          whatsNew={this.props.whatsNew}
          topShows={this.props.topShows}
          trendingVideos={this.props.trendingVideos}
          anticipatedShows={this.state.anticipatedShows}
          shortcut={this.props.shortcut}
          topic={topic}
          homepage={homepage ? homepage : this.props.homepage}
          metrics={this.props.metrics}
          history={this.props.history}
          topicId={this.props.topicId}
        />
      </>
    )
  }
}

const mapStateToProps = () => {
  const topicIdSelector = makeHomepageTopicIdSelector()
  const topicSelector = makeHomepageTopicDetailsSelector()
  const homepagePlanSelector = makeHomepageTopicPlanSelector()
  const engagementNetworkSelector = makeHomepageEngagementNetworkSelector()
  const demoSelector = makeHomepageDemographicsSelector()

  return (state, ownProps) => {
    const isAdmin = state.user.isAdmin
    const topicId = topicIdSelector(null, ownProps)
    const topic = topicSelector(state, ownProps)
    const homepagePlan = homepagePlanSelector(state, ownProps)
    const metrics = engagementNetworkSelector(state, ownProps)
    const demographics = demoSelector(state, ownProps)
    const profile = state.user.profile && new Profile(state.user.profile)
    const shortcut = ownProps.shortcut
    const homepage = state.homepage.list
    const selectedNav = state.sidebar.selectedNav
    const isContentIntelligence = state.sidebar.isContentIntelligence
    const isCatalogAnalysis = state.sidebar.isCatalogAnalysis
    const isPerformanceAnalysis = state.sidebar.isPerformanceAnalysis
    const isAIInsights = state.sidebar.isAIInsights

    return {
      isAdmin,
      profile,
      homepage,
      topicId,
      topic,
      homepagePlan,
      metrics,
      demographics,
      shortcut,
      selectedNav,
      isContentIntelligence,
      isCatalogAnalysis,
      isPerformanceAnalysis,
      isAIInsights
    }
  }
}

function mapDispatchToProps () {
  const topicIdSelector = makeHomepageTopicIdSelector()
  return (dispatch, props) => {
    const topicId = topicIdSelector(null, props)
    return {
      fetchHomepage: (groupName) => dispatch(fetchHomepageIfNeeded(groupName)),
      fetchHomepageTopicDetails: () => dispatch(fetchHomepageTopicDetailsIfNeeded(topicId)),
      fetchHomepageEngagementNetwork: () => dispatch(fetchHomepageEngagementNetworkIfNeeded(topicId)),
      fetchHomepageDemo: () => dispatch(fetchHomepageDemoIfNeeded(topicId)),
      selectedSidebarNav: (nav) => dispatch(setSelectedSidebarNav(nav)),
      logout: () => dispatch(logout())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(HomepageContainer)
